import React, { useRef, useState, useCallback } from 'react';
import { Button } from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { useUploadPhysiciansMutation } from '../../../../services/ConfigurationService';
import { useSignalR } from '../../../../hooks/useSignalR';
import { UploadProgress } from '../../../../services/signalRService';

interface PhysicianUploadCsvProps {
  company: string;
  centerId: string;  
  disabled?: boolean;
  onUploadStart?: () => void;
  onUploadComplete?: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onProgressUpdate?: (progress: UploadProgress) => void;
  onComplete?: (progress: UploadProgress) => void;
}

const PhysicianUploadCsv: React.FC<PhysicianUploadCsvProps> = ({
  company,
  centerId,
  disabled,
  onUploadStart,
  onUploadComplete,
  onSuccess,
  onError,
  onProgressUpdate,
  onComplete
}) => {
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadPhysicians, { isLoading: isUploading, error: uploadError }] = useUploadPhysiciansMutation();

  // SignalR callbacks
  const onProgress = useCallback((progress: UploadProgress) => {
    console.log('Physician upload progress:', progress);
    onProgressUpdate?.(progress);
  }, [onProgressUpdate]);

  const onCompleted = useCallback((progress: UploadProgress) => {
    console.log('Physician upload completed:', progress);
    setIsUploadInProgress(false);
    onUploadComplete?.();
    onComplete?.(progress);
    
    if (progress.status === 'Completed') {
      onSuccess?.();
    }
    // Note: For failed uploads, let onComplete handle the error display
    // Don't call onError here as it would override the detailed progress message
  }, [onUploadComplete, onComplete, onSuccess]);

  const onErrorCallback = useCallback((progress: UploadProgress) => {
    console.log('Physician upload error:', progress);
    setIsUploadInProgress(false);
    onUploadComplete?.();
    // Update progress state with detailed error information instead of calling onError
    onProgressUpdate?.(progress);
  }, [onUploadComplete, onProgressUpdate]);

  // Setup SignalR connection for Configuration service with Physician uploadType
  const { isConnected } = useSignalR({
    serviceName: 'Configuration',
    uploadType: 'Physician', // Match the UploadType constant in backend
    onProgress,
    onCompleted,
    onError: onErrorCallback,
    autoConnect: true
  });

  const handleUploadClick = () => {
    if (!company || !centerId) {
      console.warn('Company and Center ID are required');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && company && centerId) {
      try {
        // Reset progress state and start tracking
        setIsUploadInProgress(true);
        onUploadStart?.();

        const result = await uploadPhysicians({
          company,
          centerId,
          file: file
        }).unwrap();
        
        // Show upload results
        const message = `Upload completed: ${result.successful} successful, ${result.failed} failed`;
        console.log(message, result);
        
        if (result.isSuccess || result.successful > 0) {
          onSuccess?.();
        } else {
          onError?.(result.errorMessage || 'Upload failed');
        }
        
        // Clear the input so the same file can be uploaded again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        setIsUploadInProgress(false);
        onUploadComplete?.();
      } catch (err: any) {
        console.error('Upload failed:', err);
        setIsUploadInProgress(false);
        onUploadComplete?.();
        onError?.(err?.data?.message || err?.message || 'Upload failed');
      }
    }
  };

  const isDisabled = disabled || !company || !centerId || isUploading || isUploadInProgress;

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<UploadIcon />}
        onClick={handleUploadClick}
        size="small"
        disabled={isDisabled}
      >
        {isUploadInProgress ? 'Uploading...' : 'Upload CSV'}
      </Button>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".csv"
        style={{ display: 'none' }}
      />
    </>
  );
};

export default PhysicianUploadCsv;